// src/components/SelectedWorkPreview.tsx
//
// Home page "Selected work" preview.
//
// The presentation system is complete and exercised by the layout below, but
// there is no project data to show yet, so the section renders an explicitly
// marked empty state. Adding entries to homeConfig.work.projects is the only
// change required to publish work: the first `featured` project takes the
// large slot and the remainder fall into the supporting row.
//
// siteConfig.projects is deliberately not read here. It holds placeholder
// entries that the resume does not support - see the comment on homeConfig.work.
//
// There are no counts, ratings or outcome metrics anywhere in this section,
// because any number shown would have to be invented.
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { homeConfig } from '../config/homeConfig';
import type { HomeProject } from '../config/homeConfig';
import { STAGGER, motionSet } from './about/motion';
import {
  WorkRoot,
  WorkContainer,
  Header,
  Eyebrow,
  Heading,
  Lede,
  Featured,
  FeaturedVisual,
  VisualPlaceholder,
  FeaturedBody,
  MetaRow,
  TypeBadge,
  MetaDivider,
  CategoryText,
  ProjectTitle,
  ProjectDescription,
  ContextBlock,
  TechList,
  TechTag,
  ProjectLinks,
  ViewLink,
  VisuallyHidden,
  ExternalLink,
  MoreBlock,
  MoreLabel,
  SupportingGrid,
  SupportingCard,
  SupportingTitle,
  SupportingSummary,
  EmptyState,
  EmptyBadge,
  EmptyTitle,
  EmptyBody,
  CtaRow,
  Cta,
} from './SelectedWorkPreview.styles';

const headingId = 'home-work-heading';
const viewport = { once: true, amount: 0.3 } as const;

/**
 * Where a project's own detail page will live. Placeholder until project
 * routes exist - `homeConfig.work.projects[].id` becomes the slug.
 */
const projectHref = (id: string) => `/projects/${id}`;

const TechTags = ({ items, label }: { items: string[]; label: string }) => {
  if (items.length === 0) return null;

  return (
    <TechList aria-label={label}>
      {items.map((tech) => (
        <TechTag key={tech}>{tech}</TechTag>
      ))}
    </TechList>
  );
};

const FeaturedProject = ({ project, viewLabel }: { project: HomeProject; viewLabel: string }) => (
  <Featured>
    <FeaturedVisual>
      {project.visual ? (
        <img src={project.visual} alt={`${project.name} screenshot`} loading="lazy" />
      ) : (
        <VisualPlaceholder>Visual pending</VisualPlaceholder>
      )}
    </FeaturedVisual>

    <FeaturedBody>
      <MetaRow>
        <TypeBadge>{project.type}</TypeBadge>
        {project.category && (
          <>
            <MetaDivider aria-hidden="true">/</MetaDivider>
            <CategoryText>{project.category}</CategoryText>
          </>
        )}
      </MetaRow>

      <ProjectTitle>{project.name}</ProjectTitle>

      {(project.description ?? project.summary) && (
        <ProjectDescription>{project.description ?? project.summary}</ProjectDescription>
      )}

      {project.context && <ContextBlock>{project.context}</ContextBlock>}

      <TechTags items={project.technologies} label="Technologies" />

      <ProjectLinks>
        <ViewLink href={projectHref(project.id)}>
          {viewLabel}
          <VisuallyHidden>: {project.name}</VisuallyHidden>
        </ViewLink>
        {project.links?.github && (
          <ExternalLink href={project.links.github} target="_blank" rel="noopener noreferrer">
            GitHub
          </ExternalLink>
        )}
        {project.links?.demo && (
          <ExternalLink href={project.links.demo} target="_blank" rel="noopener noreferrer">
            Live demo
          </ExternalLink>
        )}
      </ProjectLinks>
    </FeaturedBody>
  </Featured>
);

const SupportingProject = ({ project, viewLabel }: { project: HomeProject; viewLabel: string }) => (
  <SupportingCard>
    <MetaRow>
      <TypeBadge>{project.type}</TypeBadge>
    </MetaRow>
    <SupportingTitle>{project.name}</SupportingTitle>
    <SupportingSummary>{project.summary}</SupportingSummary>
    <TechTags items={project.technologies} label="Technologies" />
    <ProjectLinks>
      <ViewLink href={projectHref(project.id)}>
        {viewLabel}
        <VisuallyHidden>: {project.name}</VisuallyHidden>
      </ViewLink>
    </ProjectLinks>
  </SupportingCard>
);

const SelectedWorkPreview = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const navigate = useNavigate();
  const anim = motionSet(reduceMotion);

  const section = homeConfig.work;
  const { projects } = section;

  // One project takes the large slot: the first flagged as featured, else the
  // first entry. The remainder become the supporting row.
  const featured = projects.find((project) => project.featured) ?? projects[0];
  const supporting = featured ? projects.filter((project) => project.id !== featured.id) : [];

  return (
    <WorkRoot aria-labelledby={headingId}>
      <WorkContainer>
        <Header
          variants={anim.group(STAGGER.group)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <Eyebrow variants={anim.reveal}>{section.eyebrow}</Eyebrow>
          <Heading id={headingId} variants={anim.reveal}>
            {section.heading}
          </Heading>
          <Lede variants={anim.reveal}>{section.lede}</Lede>
        </Header>

        {featured ? (
          <motion.div
            key={featured.id}
            variants={anim.reveal}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <FeaturedProject project={featured} viewLabel={section.viewLabel} />
          </motion.div>
        ) : (
          <EmptyState variants={anim.reveal} initial="hidden" whileInView="visible" viewport={viewport}>
            <EmptyBadge>{section.empty.badge}</EmptyBadge>
            <EmptyTitle>{section.empty.title}</EmptyTitle>
            <EmptyBody>{section.empty.body}</EmptyBody>
          </EmptyState>
        )}

        {supporting.length > 0 && (
          <MoreBlock>
            <MoreLabel>{section.moreLabel}</MoreLabel>
            <SupportingGrid>
              {supporting.map((project) => (
                <motion.div
                  key={project.id}
                  variants={anim.reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                >
                  <SupportingProject project={project} viewLabel={section.viewLabel} />
                </motion.div>
              ))}
            </SupportingGrid>
          </MoreBlock>
        )}

        {/* The section CTA is suppressed while the section is empty: with no
            projects it would be a third link to /projects, sitting directly
            above the closing CTA's "View My Work" to the same route. The empty
            state already points onward, and /projects stays reachable from the
            closing CTA. It returns as soon as there is a project to list. */}
        {projects.length > 0 && (
          <CtaRow variants={anim.reveal} initial="hidden" whileInView="visible" viewport={viewport}>
            <Cta type="button" onClick={() => navigate(section.cta.to)}>
              {section.cta.label}
            </Cta>
          </CtaRow>
        )}
      </WorkContainer>
    </WorkRoot>
  );
};

export default SelectedWorkPreview;
